import React from 'react';
import moment from 'moment';
import {
    FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaFileArchive,
    FaFileAudio, FaFileVideo, FaFileAlt,
} from 'react-icons/fa';

// Shared presentation helpers for the session pages — the public participant
// page (PublicSession) and the internal, logged-in view (SessionViewPage) use
// the same date/link/file-icon rendering so they never drift apart.

// Pick a file-type icon from the name/mime so each file reads at a glance.
export const fileIconFor = (name = '', mimeType = '') => {
    const ext = String(name).split('.').pop().toLowerCase();
    const m = String(mimeType).toLowerCase();
    if (m.includes('pdf') || ext === 'pdf') { return FaFilePdf; }
    if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic'].includes(ext)) { return FaFileImage; }
    if (m.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) { return FaFileVideo; }
    if (m.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) { return FaFileAudio; }
    if (m.includes('word') || ['doc', 'docx'].includes(ext)) { return FaFileWord; }
    if (m.includes('sheet') || m.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) { return FaFileExcel; }
    if (m.includes('zip') || m.includes('compress') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) { return FaFileArchive; }
    return FaFileAlt;
};

// Build a { dateKicker, dateMain, timeMain } block from start/end dates.
export const buildDateTime = (c, lang) => {
    const start = c?.startDate ? moment(c.startDate).locale(lang) : null;
    const end = c?.endDate ? moment(c.endDate).locale(lang) : null;
    if (!start && !end) { return null; }

    const hasTime = (m) => m && (m.hours() !== 0 || m.minutes() !== 0);
    const s = start || end;
    const e = end || start;
    const sameDay = s.isSame(e, 'day');

    let dateKicker;
    let dateMain;
    if (sameDay) {
        dateKicker = s.format('dddd');
        dateMain = s.format('D MMM YYYY');
    } else {
        dateKicker = `${s.format('ddd')} → ${e.format('ddd')}`;
        dateMain = `${s.format('D MMM')} → ${e.format('D MMM YYYY')}`;
    }

    let timeMain = '';
    if (start && end && sameDay && (hasTime(start) || hasTime(end))) {
        timeMain = `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
    } else if (hasTime(s)) {
        timeMain = s.format('HH:mm');
    }

    return { dateKicker, dateMain, timeMain };
};

// Turn plain text into React nodes with bare URLs linkified.
const URL_RE = /(https?:\/\/[^\s<]+)/g;
export const renderWithLinks = (text) => {
    if (!text) { return text; }
    const parts = [];
    let lastIndex = 0;
    let match;
    URL_RE.lastIndex = 0;
    while ((match = URL_RE.exec(text)) !== null) {
        const raw = match[0];
        const start = match.index;
        if (start > lastIndex) { parts.push(text.slice(lastIndex, start)); }
        const trail = raw.match(/[.,;:!?)\]]+$/);
        const href = trail ? raw.slice(0, -trail[0].length) : raw;
        parts.push(
            <a key={start} href={href} target="_blank" rel="noopener noreferrer">
                {href}
            </a>
        );
        if (trail) { parts.push(trail[0]); }
        lastIndex = start + raw.length;
    }
    if (lastIndex < text.length) { parts.push(text.slice(lastIndex)); }
    return parts;
};
